import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface PasswordEntry {
  id: string;
  user_id: number;
  account_name: string;
  website_url?: string;
  username?: string;
  password?: string; // Optional because list endpoint doesn't return passwords
  notes?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
}

export interface CreatePasswordData {
  id?: string;
  account_name: string;
  website_url?: string;
  username?: string;
  password: string;
  notes?: string;
}

export interface UpdatePasswordData {
  account_name?: string;
  website_url?: string;
  username?: string;
  password?: string;
  notes?: string;
}

export interface PasswordFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

class PasswordService {
  // Get all passwords
  async getPasswords(filters?: PasswordFilters): Promise<{ success: boolean; passwords?: PasswordEntry[]; count?: number; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/passwords${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching passwords:', error);
      throw error;
    }
  }

  // Get password by ID
  async getPasswordById(id: string): Promise<{ success: boolean; password?: PasswordEntry; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/passwords/${id}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching password:', error);
      throw error;
    }
  }

  // Create a new password entry
  async createPassword(data: CreatePasswordData): Promise<{ success: boolean; id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/passwords', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating password entry:', error);
      throw error;
    }
  }

  // Update a password entry
  async updatePassword(id: string, data: UpdatePasswordData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/passwords/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating password entry:', error);
      throw error;
    }
  }

  // Delete a password entry
  async deletePassword(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/passwords/${id}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting password entry:', error);
      throw error;
    }
  }
}

export const passwordService = new PasswordService();

